import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site`);
      setContent(data);
    } catch (e) {
      console.warn("Failed to load site content; using fallbacks.", e?.message);
      setContent(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteContentContext.Provider value={{ content, loaded, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  return ctx || { content: null, loaded: false, refresh: () => {} };
}

/** Merge dynamic content into i18n.t.services.items by slug. Falls back to original i18n entry. */
export function mergeServices(i18nItems, lang, dynamicServices) {
  if (!Array.isArray(dynamicServices) || dynamicServices.length === 0) return i18nItems;
  return i18nItems.map((base) => {
    const dyn = dynamicServices.find((s) => s.slug === base.slug);
    if (!dyn) return base;
    const pick = (k) => (dyn[k] && dyn[k][lang]) || base[k];
    return {
      ...base,
      eyebrow: pick("eyebrow"),
      t: pick("title"),
      d: pick("description"),
      long: pick("long"),
      bullets: (dyn.bullets && dyn.bullets[lang]) || base.bullets,
      image: dyn.image || null,
    };
  });
}

/** Merge dynamic home content into i18n.t.home (text only, keeps pillars/whyItems from i18n). */
export function mergeHome(i18nHome, lang, dynamicHome) {
  if (!dynamicHome) return i18nHome;
  const p = (field, fallback) => pickLang(field, lang, fallback);
  const hero = dynamicHome.hero || {};
  const philosophy = dynamicHome.philosophy || {};
  const why = dynamicHome.why || {};
  const finalCta = dynamicHome.finalCta || {};
  const stats = dynamicHome.stats || {};

  const mergedStats = { ...i18nHome.stats };
  for (const k of ["programs", "clients", "participants", "years"]) {
    const s = stats[k];
    if (s && s.label) mergedStats[k] = p(s.label, mergedStats[k]);
  }

  const statsValues = {};
  for (const k of ["programs", "clients", "participants", "years"]) {
    if (stats[k] && typeof stats[k].value !== "undefined") {
      statsValues[k] = { value: stats[k].value, suffix: stats[k].suffix ?? "" };
    }
  }

  // Pillars: dynamic override varsa kullan, yoksa i18n'den al
  const pillars = (dynamicHome.pillars && Array.isArray(dynamicHome.pillars) && dynamicHome.pillars.length > 0)
    ? dynamicHome.pillars.map((dp, i) => ({
        t: p(dp.title, i18nHome.pillars[i]?.t || ""),
        d: p(dp.description, i18nHome.pillars[i]?.d || ""),
      }))
    : i18nHome.pillars;

  // WhyItems: dynamic override varsa kullan, yoksa i18n'den al
  const whyItems = (dynamicHome.whyItems && Array.isArray(dynamicHome.whyItems) && dynamicHome.whyItems.length > 0)
    ? dynamicHome.whyItems.map((dw, i) => ({
        t: p(dw.title, i18nHome.whyItems[i]?.t || ""),
        d: p(dw.description, i18nHome.whyItems[i]?.d || ""),
      }))
    : i18nHome.whyItems;

  return {
    ...i18nHome,
    eyebrow: p(hero.eyebrow, i18nHome.eyebrow),
    heroTitle: p(hero.title, i18nHome.heroTitle),
    heroLead: p(hero.lead, i18nHome.heroLead),
    ctaPrimary: p(hero.ctaPrimary, i18nHome.ctaPrimary),
    ctaSecondary: p(hero.ctaSecondary, i18nHome.ctaSecondary),
    philosophyEyebrow: p(philosophy.eyebrow, i18nHome.philosophyEyebrow),
    philosophyTitle: p(philosophy.title, i18nHome.philosophyTitle),
    philosophyBody: p(philosophy.body, i18nHome.philosophyBody),
    whyEyebrow: p(why.eyebrow, i18nHome.whyEyebrow),
    whyTitle: p(why.title, i18nHome.whyTitle),
    finalCtaTitle: p(finalCta.title, i18nHome.finalCtaTitle),
    finalCtaBody: p(finalCta.body, i18nHome.finalCtaBody),
    stats: mergedStats,
    statsValues,
    pillars,
    whyItems,
  };
}

/** Resolve a bilingual field. */
export function pickLang(field, lang, fallback = "") {
  if (!field) return fallback;
  if (typeof field === "string") return field;
  return field[lang] || field.tr || field.en || fallback;
}
